import { useCallback, useEffect, useRef, useState } from 'react';
import { refusalMessage } from '../api';
import { ringToneSync as wiredSync } from '../lib/ringTones';
import { ringToneStore } from '../lib/nativePermissions';

const BUILT_IN = 'InGo Pulse';

// Made once: a store made in the default parameter is a new object each render, the
// effect below re-ran on every one, and the picker re-synced every 120 ms (the emulator).
const wiredStore = ringToneStore();

function PlayIcon({ playing }) {
  return playing ? (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
      <rect x="6" y="6" width="12" height="12" rx="1.5" fill="currentColor" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
      <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
    </svg>
  );
}

/**
 * The tone a job rings with on this phone (ADR 0014; InGo ADR 0045): the
 * office's default, or another tone the office switched on when it lets
 * riders choose. Each is sampled at the ringer's volume, as a job would
 * ring. Opening it syncs the tones, so what is listed is on the phone.
 *
 * Only in the app: the website has no ring core and rings in the page.
 */
export default function RingtonePicker({ sync = wiredSync, store = wiredStore }) {
  const [state, setState] = useState({ phase: 'loading' });
  // The choice being saved: shown at once, rolled back if the server refuses it.
  const [picking, setPicking] = useState(null);
  const [said, setSaid] = useState('');
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(null);
  const [ringerOn, setRingerOn] = useState(true);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (!store) return undefined;
    (async () => {
      const done = await sync.sync('picker');
      if (!mounted.current) return;
      if (!done) {
        setState({ phase: 'failed' });
        return;
      }
      setState({ phase: 'ready', listing: done.listing, have: done.result?.have ?? [] });
      try {
        const phone = await store.getRingTones();
        if (mounted.current) setRingerOn(phone?.ringerOn !== false);
      } catch {
        // The ringer's state is a hint; the picker works without it.
      }
    })();
    return () => {
      mounted.current = false;
      store.stopRingTonePreview().catch(() => {});
    };
  }, [store, sync]);

  const play = useCallback(
    async (key, id) => {
      if (playing === key) {
        setPlaying(null);
        await store.stopRingTonePreview().catch(() => {});
        return;
      }
      setPlaying(key);
      setError('');
      try {
        const r = await store.previewRingTone(id);
        if (!r?.playing) {
          setPlaying(null);
          setError('That tone is not on this phone yet. Check your connection and open this page again.');
        }
      } catch {
        setPlaying(null);
      }
    },
    [playing, store],
  );

  const choose = useCallback(
    async (id) => {
      setPicking({ id });
      setSaid('');
      setError('');
      try {
        const listing = await sync.choose(id);
        const tone = (listing?.tones ?? []).find((t) => t.id === (id ?? listing?.default_id));
        setState((s) => ({ ...s, listing }));
        setSaid(`Saved. Jobs will ring with ${tone?.name ?? BUILT_IN}.`);
      } catch (e) {
        setError(refusalMessage(e) || 'Your ringtone was not saved. Check your connection and try again.');
      } finally {
        setPicking(null);
      }
    },
    [sync],
  );

  if (!store) return null;

  if (state.phase === 'loading') {
    return <p className="dn-status">Loading the ringtones…</p>;
  }

  if (state.phase === 'failed') {
    return (
      <p className="dn-flash dn-flash--err" role="alert">
        The ringtones could not be loaded. Jobs ring with the last tone this phone has. Check your connection and open this page again.
      </p>
    );
  }

  const { listing, have } = state;
  const tones = listing?.tones ?? [];
  const defaultTone = tones.find((t) => t.id === listing?.default_id) ?? null;
  const defaultName = defaultTone?.name ?? BUILT_IN;
  const chosen = picking ? picking.id : listing?.chosen_id ?? null;

  const row = (key, id, sample, name, detail, selectable) => (
    <li key={key} className="dn-tone">
      <button
        type="button"
        className="dn-tone__play"
        aria-label={`${playing === key ? 'Stop' : 'Play'} ${name}`}
        aria-pressed={playing === key}
        onClick={() => void play(key, sample)}
      >
        <PlayIcon playing={playing === key} />
      </button>
      {selectable ? (
        <label className="dn-tone__choice" htmlFor={`dn-tone-${key}`}>
          <span className="dn-tone__text">
            <span className="dn-tone__name">{name}</span>
            {detail ? (
              <span id={`dn-tone-${key}-hint`} className="dn-tone__hint">
                {detail}
              </span>
            ) : null}
          </span>
          <input
            id={`dn-tone-${key}`}
            type="radio"
            aria-label={name}
            aria-describedby={detail ? `dn-tone-${key}-hint` : undefined}
            name="dn-ringtone"
            className="dn-tone__radio"
            checked={(id === null && chosen === null) || (id !== null && chosen === id)}
            disabled={picking !== null}
            onChange={() => void choose(id)}
          />
        </label>
      ) : (
        <span className="dn-tone__text">
          <span className="dn-tone__name">{name}</span>
          {detail ? <span className="dn-tone__hint">{detail}</span> : null}
        </span>
      )}
    </li>
  );

  const notHere = (t) => (have.includes(t.id) ? '' : 'Not on this phone yet');

  return (
    <section className="dn-card" aria-labelledby="dn-ringtone-h">
      <h2 id="dn-ringtone-h" className="dn-card__h">
        Ringtone
      </h2>
      {!ringerOn ? (
        <p className="dn-tone__note" role="status">
          Your phone&apos;s ringer is off: a job will vibrate, not ring.
        </p>
      ) : null}
      {listing?.may_choose && tones.length > 0 ? (
        <ul className="dn-tones" aria-label="Ringtones">
          {row('default', null, defaultTone?.id ?? null, `The office's tone: ${defaultName}`, defaultTone ? notHere(defaultTone) || 'Changes when the office changes it' : 'Built into the app', true)}
          {tones.map((t) => row(String(t.id), t.id, t.id, t.name, notHere(t), true))}
        </ul>
      ) : (
        <ul className="dn-tones" aria-label="Ringtones">
          {row('default', null, defaultTone?.id ?? null, defaultName, tones.length > 0 ? 'The office sets the ringtone for every rider.' : "Your phone rings with InGo's built-in tone.", false)}
        </ul>
      )}
      {said ? (
        <p className="dn-flash" role="status">
          {said}
        </p>
      ) : null}
      {error ? (
        <p className="dn-flash dn-flash--err" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
